import { resolveImageUrl } from "@/utils/imageRef";

export const SUPPORTED_IMAGE_ASPECT_RATIOS = ["1:1", "4:5", "3:4", "16:9"] as const;

export type SupportedImageAspectRatio = (typeof SUPPORTED_IMAGE_ASPECT_RATIOS)[number];

export const DEFAULT_IMAGE_ASPECT_RATIO: SupportedImageAspectRatio = "3:4";

const ASPECT_RATIO_VALUES: Record<SupportedImageAspectRatio, number> = {
  "1:1": 1,
  "4:5": 4 / 5,
  "3:4": 3 / 4,
  "16:9": 16 / 9,
};

const aspectRatioCache = new Map<string, Promise<SupportedImageAspectRatio>>();

export function closestSupportedAspectRatio(width: number, height: number): SupportedImageAspectRatio {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return DEFAULT_IMAGE_ASPECT_RATIO;
  }

  const ratio = width / height;
  let best: SupportedImageAspectRatio = DEFAULT_IMAGE_ASPECT_RATIO;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of SUPPORTED_IMAGE_ASPECT_RATIOS) {
    const distance = Math.abs(ASPECT_RATIO_VALUES[candidate] - ratio);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }

  return best;
}

async function loadImageDimensions(url: string): Promise<{ width: number; height: number }> {
  const img = new Image();
  img.src = url;

  if (img.complete && (img.naturalWidth || img.width) && (img.naturalHeight || img.height)) {
    return {
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
    };
  }

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to load image dimensions"));
  });

  return {
    width: img.naturalWidth || img.width,
    height: img.naturalHeight || img.height,
  };
}

export async function inferImageAspectRatio(url: string | null | undefined): Promise<SupportedImageAspectRatio> {
  const normalizedUrl = resolveImageUrl(String(url ?? "").trim());
  if (!normalizedUrl) {
    return DEFAULT_IMAGE_ASPECT_RATIO;
  }

  const cached = aspectRatioCache.get(normalizedUrl);
  if (cached) {
    return cached;
  }

  const pending = loadImageDimensions(normalizedUrl)
    .then(({ width, height }) => closestSupportedAspectRatio(width, height))
    .catch(() => DEFAULT_IMAGE_ASPECT_RATIO);

  aspectRatioCache.set(normalizedUrl, pending);
  return pending;
}
